import { debug } from '@skinsoft/debug';
import localforage from 'localforage';
import { isEqual } from 'lodash';
import { from, merge, of, Subject } from 'rxjs';
import { concatMap, distinctUntilChanged, filter, tap, throttleTime } from 'rxjs/operators';

import { Cache } from './types';

export const indexeddb = localforage.createInstance({
    driver: localforage.INDEXEDDB,
    name: 'reporter-tracking',
    storeName: 'runtime_cache',
    version: 2.0,
});

export class IndexedDBCache<T> implements Cache<T> {
    public static clearStorage = () => indexeddb.clear();
    private static debug = debug('indexeddb-cache');

    private tick$ = new Subject<string>();

    public notifyOn(pattern: RegExp) {
        return this.tick$.pipe(
            filter(uri => pattern.test(uri)),
            throttleTime(100),
            distinctUntilChanged(),
        );
    }

    public read(uri: string) {
        return merge(of(null), this.tick$.pipe(filter(value => value === uri))).pipe(
            concatMap(() => from(this.getItem(uri) as Promise<T[]>)),
            tap(value => {
                IndexedDBCache.debug('read', uri, value);
            }),
        );
    }

    public remove(uri: string) {
        IndexedDBCache.debug('remove', uri);
        indexeddb.removeItem(uri).then(() => this.tick(uri));
    }

    public async write(uri: string, data: T[]) {
        const value = await this.getItem(uri);
        if (isEqual(value, data)) {
            return;
        }

        IndexedDBCache.debug('write', uri);
        indexeddb.setItem(uri, data).then(() => this.tick(uri));
    }

    private tick(uri: string) {
        this.tick$.next(uri);
    }

    private async getItem(uri: string) {
        const value = await indexeddb.getItem(uri);
        return value || undefined;
    }
}
